<script setup lang="ts">
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const { getMerchantAttributes } = useLuxuryDesign();
const { hideRecommendedPrice } = useRuntimeConfig().public;

const collectibleDesign = computed(() =>
  getMerchantAttributes(props.collectible.merchantId)
);

const { getCollectibleHighestOffer } = useCollectiblesController();
const GFALExStore = useGFALExStore();
const userStore = useUserStore();
const isHovered = ref<boolean>(false);
const showOfferBadge = ref<boolean>(false);

const checkOffers = async () => {
  if (!userStore.isLoggedIn) return;
  if (userStore.isCurrentUserId(props.collectible.gfalId)) {
    const res = await getCollectibleHighestOffer(props.collectible.id);
    if (res) {
      showOfferBadge.value = true;
    }
  }
};

checkOffers();
</script>

<template>
  <div
    v-bind="collectibleDesign"
    class="relative grid grid-rows-[1fr,1fr,1fr,1fr, 1fr] border rounded-lg border-primary-900 will-change-transform transition ease-in-out duration-300 overflow-hidden collectible-card"
    :class="{ '-translate-y-1': isHovered }"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false">
    <div class="relative overflow-hidden">
      <div
        v-if="collectible.price?.GFAL"
        class="absolute bottom-4 right-0 bg-error-300 text-xs text-neutral-200 font-bold text-center w-fit px-4 py-1 rounded-tl-2xl rounded-bl z-20">
        {{ $t("main.cardCollections.listed") }}
      </div>
      <div
        v-if="userStore.isCurrentUserId(collectible.gfalId) && collectible.new"
        class="absolute bottom-4 right-0 bg-error-300 text-xs text-neutral-200 font-bold text-center w-fit px-4 py-1 rounded-tl-2xl rounded-bl z-20">
        {{ $t("main.cardCollections.new") }}
      </div>
      <div
        v-if="collectible.level"
        class="absolute top-2 right-2 text-2xl text-neutral-200 font-bold text-center w-fit rounded-lg flex items-center justify-center collectible-card__level">
        <BaseIconHexagon class="w-10 h-10" />
        <p class="absolute z-10">{{ collectible.level }}</p>
      </div>
      <div
        v-if="collectible.number"
        class="absolute top-4 left-2 bg-neutral-500 border border-neutral-400 text-xs text-neutral-200 font-bold text-center w-fit px-2.5 py-1 rounded-lg">
        #{{ collectible.number }}
      </div>
      <HeadlessTransitionRoot
        :show="showOfferBadge"
        enter="transition-opacity duration-1000"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity duration-1000"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <BaseIconOfferStamp
          class="w-10 h-10 absolute bottom-2 left-3 z-20"
          :class="isHovered && 'animate-pulse'" />
      </HeadlessTransitionRoot>
      <CardImage :src="getPortraitItemImage(collectible.imageId)" />
    </div>
    <div class="w-full grid grid-rows-[4px_2px] z-10 collectible-card__rarity">
      <div :class="getRarityColor(collectible.rarity, 'bg')"></div>
      <div
        class="opacity-80"
        :class="getRarityColor(collectible.rarity, 'bg')"></div>
    </div>
    <div
      class="max-w-full px-2 py-3.5 border-t border-primary-900 text-center truncate merchant-card__surface">
      <h1 class="text-neutral-200 text-lg font-bold truncate merchant-heading">
        {{ $t(`${collectible.translationId}.title`) }}
        {{ $t(`${collectible.translationId}.subtitle`) }}
      </h1>
    </div>
    <div
      v-if="
        collectible.price?.GFAL || hideRecommendedPrice.toString() !== 'true'
      "
      class="relative flex flex-col gap-1 rounded-b-lg border-t border-primary-900 shadow-xs focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 transition-all duration-300 ease-in-out transform merchant-card__surface">
      <div class="px-2 py-2 flex justify-between">
        <template v-if="collectible.price?.GFAL">
          <p class="text-secondary-200 font-semibold text-xs pt-1">
            {{ $t("main.cardCollections.listedFor") }}
          </p>
          <div class="flex flex-col items-end">
            <div class="flex gap-2">
              <BaseIcon
                name="GFAL_token"
                class="w-4"
                :alt="$t('main.imageAltTexts.GFALTokenLogo')" />
              <p
                v-if="collectible.price?.GFAL"
                class="text-neutral-200 font-bold">
                {{ roundNum(collectible.price.GFAL) }}
              </p>
            </div>
            <p
              v-if="collectible.price?.USD"
              class="text-neutral-300 text-xs font-semibold">
              {{ "$" + roundNum(collectible.price.USD) }}
            </p>
          </div>
        </template>
        <template v-else>
          <p class="text-secondary-200 font-semibold text-xs pt-1">
            {{ $t("main.cardCollections.recommendedPrice") }}
          </p>
          <div class="flex flex-col items-end">
            <div class="flex gap-2">
              <BaseIcon
                name="GFAL_token"
                class="w-4"
                :alt="$t('main.imageAltTexts.GFALTokenLogo')" />
              <p class="text-neutral-200 font-bold">
                {{ roundNum(collectible.recommendedPrice.GFAL) }}
              </p>
            </div>
            <p class="text-neutral-300 text-xs font-semibold">
              ${{
                roundNum(
                  collectible.recommendedPrice.GFAL * GFALExStore.GFALExRate
                )
              }}
            </p>
          </div>
        </template>
      </div>
    </div>
    <div
      v-else
      class="max-w-full px-2 py-7 border-t border-primary-900 text-center truncate bg-neutral-800"></div>
  </div>
</template>
